import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, Check, X, Edit2, CalendarIcon as CalIcon, Receipt, Package, DollarSign, List, Grid, Rows, Plane, Home, Coffee, Calendar } from 'lucide-react';
import { formatCurrency, getDateKey } from '../../utils/formatters';
import { CardSkeleton } from '../common/Skeleton';
import ShowFormComponent, { getEventColorConfig } from '../common/ShowFormComponent';
import ContactModal from './ContactModal';
import ShowDetailModal from '../common/ShowDetailModal';
import { AVAILABLE_TAGS, getTagColor, getTagLabel } from '../../utils/contactTags';
import { AddShowGate } from '../subscription/FeatureGates';
import { useSwipeToClose } from '../../hooks/useSwipeToClose';


const isHapticSupported = () => typeof navigator !== 'undefined' && 'vibrate' in navigator;
const haptics = {
  light: () => { if (isHapticSupported()) navigator.vibrate(10); },
  medium: () => { if (isHapticSupported()) navigator.vibrate(25); },
  heavy: () => { if (isHapticSupported()) navigator.vibrate(50); },
  success: () => { if (isHapticSupported()) navigator.vibrate([10, 50, 10]); },
  error: () => { if (isHapticSupported()) navigator.vibrate([50, 30, 50, 30, 50]); },
  warning: () => { if (isHapticSupported()) navigator.vibrate([30, 50, 30]); },
  selection: () => { if (isHapticSupported()) navigator.vibrate(5); },
  impact: () => { if (isHapticSupported()) navigator.vibrate(40); },
};

const PERSONAL_CATEGORY_CONFIG = {
  travel: { color: 'bg-blue-400 dark:bg-blue-600', textColor: 'text-blue-800 dark:text-blue-200', bgLight: 'bg-blue-100 dark:bg-blue-900/30', border: 'border-blue-400', icon: Plane, label: 'Travel' },
  dayoff: { color: 'bg-green-400 dark:bg-green-600', textColor: 'text-green-800 dark:text-green-200', bgLight: 'bg-green-100 dark:bg-green-900/30', border: 'border-green-400', icon: Coffee, label: 'Day Off' },
  home: { color: 'bg-purple-400 dark:bg-purple-600', textColor: 'text-purple-800 dark:text-purple-200', bgLight: 'bg-purple-100 dark:bg-purple-900/30', border: 'border-purple-400', icon: Home, label: 'Home' },
  other: { color: 'bg-gray-400 dark:bg-gray-600', textColor: 'text-gray-800 dark:text-gray-200', bgLight: 'bg-gray-100 dark:bg-gray-800', border: 'border-gray-400', icon: Calendar, label: 'Other' },
};

const EVENT_COLOR_MAP = {
  orange: { bg: 'bg-orange-500', bgLight: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-200', border: 'border-orange-400' },
  purple: { bg: 'bg-purple-500', bgLight: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-200', border: 'border-purple-400' },
  green: { bg: 'bg-green-500', bgLight: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-200', border: 'border-green-400' },
  pink: { bg: 'bg-pink-500', bgLight: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-800 dark:text-pink-200', border: 'border-pink-400' },
  cyan: { bg: 'bg-cyan-500', bgLight: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-800 dark:text-cyan-200', border: 'border-cyan-400' },
  yellow: { bg: 'bg-yellow-500', bgLight: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-200', border: 'border-yellow-400' },
  red: { bg: 'bg-red-500', bgLight: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-200', border: 'border-red-400' },
  indigo: { bg: 'bg-indigo-500', bgLight: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-200', border: 'border-indigo-400' },
};

const CalendarComponent = ({ shows, onNavigate, onAddShow, onUpdateShow, onDeleteShow, onToggleShow, loading, documents = [], salesLog = [] }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingShow, setEditingShow] = useState(null);
  const [viewMode, setViewMode] = useState('month');
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [showForm, setShowForm] = useState({
    title: '', venue: '', city: '', loadInTime: '', showTime: '', guarantee: '',
    cashMerch: '', venmoMerch: '', contactName: '', contactEmail: '', contactPhone: '',
    contactTags: [], eventType: 'show', personalCategory: 'travel',
    startTime: '', endTime: '', eventColor: 'orange',
    gas: '', hotel: '', food: '', tolls: '', otherExpenses: '', showNotes: '',
    setlist: '', crowdInfo: '', technicalNotes: '', nextTime: '', status: 'confirmed', images: [], 
  });
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [deletedShow, setDeletedShow] = useState(null);
  const [undoTimeout, setUndoTimeout] = useState(null);

  // Define handleCloseMobileDetail FIRST (before the swipe hook)
  const handleCloseMobileDetail = useCallback(() => { 
    haptics.light(); 
    setMobileDetailOpen(false); 
  }, []);

  const useSwipeNavigation = (onSwipeLeft, onSwipeRight, options = {}) => {
  const { threshold = 50 } = options;
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [deltaX, setDeltaX] = useState(0);
  const [swiping, setSwiping] = useState(false);

  const onTouchStart = useCallback((e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setSwiping(true);
    setDeltaX(0);
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!touchStart) return;
    const currentX = e.targetTouches[0].clientX;
    setDeltaX(currentX - touchStart);
    setTouchEnd(currentX);
  }, [touchStart]);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) {
      setSwiping(false);
      setDeltaX(0);
      return;
    }
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) > threshold) {
      if (distance > 0) {
        onSwipeLeft?.();
      } else {
        onSwipeRight?.();
      }
    }
    setSwiping(false);
    setDeltaX(0);
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, threshold, onSwipeLeft, onSwipeRight]);

  return {
    swipeHandlers: { onTouchStart, onTouchMove, onTouchEnd },
    swipeStyle: swiping ? {
      transform: `translateX(${deltaX * 0.3}px)`,
      transition: 'none'
    } : {
      transform: 'translateX(0)',
      transition: 'transform 0.2s ease-out'
    },
    swiping
  };
};

  // NOW use the swipe hook (after handleCloseMobileDetail is defined)
  const { 
    dragOffset, 
    isDragging, 
    handleTouchStart, 
    handleTouchMove, 
    handleTouchEnd 
  } = useSwipeToClose(handleCloseMobileDetail, { threshold: 80 });

  // Horizontal swipe navigation for month view (swipe left = next, swipe right = previous)
  const { 
    swipeHandlers: monthSwipeHandlers, 
    swipeStyle: monthSwipeStyle 
  } = useSwipeNavigation(
    () => { haptics.light(); setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1)); },
    () => { haptics.light(); setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1)); },
    { threshold: 50 }
  );

  // Horizontal swipe navigation for week view
  const { 
    swipeHandlers: weekSwipeHandlers, 
    swipeStyle: weekSwipeStyle 
  } = useSwipeNavigation(
    () => { haptics.light(); setCurrentDate(prev => { const d = new Date(prev); d.setDate(d.getDate() + 7); return d; }); },
    () => { haptics.light(); setCurrentDate(prev => { const d = new Date(prev); d.setDate(d.getDate() - 7); return d; }); },
    { threshold: 50 }
  );

  //lock page in place

  useEffect(() => {
  const isMobile = window.innerWidth < 768;
  if (isMobile) {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
  }
  
  return () => {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.height = '';
  };
}, []);

  // Disable pull-to-refresh on mobile
  useEffect(() => {
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overscrollBehavior = '';
      document.documentElement.style.overscrollBehavior = '';
    };
  }, []);

  const formatTime12h = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleDeleteWithUndo = (dateKey, showId, show) => {
    haptics.warning();
    if (undoTimeout) clearTimeout(undoTimeout);
    setDeletedShow({ dateKey, show });
    setShowDetailModal(null);
    onDeleteShow(dateKey, showId);
    const timeout = setTimeout(() => setDeletedShow(null), 5000);
    setUndoTimeout(timeout);
  };

  const handleUndoDelete = () => {
    if (deletedShow) {
      haptics.success();
      onAddShow(deletedShow.dateKey, deletedShow.show);
      setDeletedShow(null);
      if (undoTimeout) { clearTimeout(undoTimeout); setUndoTimeout(null); }
    }
  };

  useEffect(() => {
    if (mobileDetailOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileDetailOpen]);

  const showFormRef = useRef(showForm);
  useEffect(() => { showFormRef.current = showForm; }, [showForm]);

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const daysOfWeekShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isPastOrCompleted = useMemo(() => {
    if (!selectedDate) return false;
    const selectedDateObj = new Date(selectedDate.year, selectedDate.month, selectedDate.day);
    selectedDateObj.setHours(0, 0, 0, 0);
    if (selectedDateObj < today) return true;
    if (showForm.status === 'completed') return true;
    return false;
  }, [selectedDate, showForm.status, today]);

  const getShowStatusColors = (show, dateKey) => {
    const showDate = new Date(dateKey);
    const isPast = showDate < today;
    
    if (show.eventType === 'personal' || show.eventType === 'other') {
      if (isPast) {
        return { bg: 'bg-gray-100 dark:bg-gray-800', border: 'border-gray-400 dark:border-gray-600', text: 'text-gray-500 dark:text-gray-400', bgSolid: 'bg-gray-300 dark:bg-gray-600', isPast: true, isPersonal: true, label: show.title || 'Other' };
      }
      if (show.eventColor && EVENT_COLOR_MAP[show.eventColor]) {
        const colorConfig = EVENT_COLOR_MAP[show.eventColor];
        return { bg: colorConfig.bgLight, border: colorConfig.border, text: colorConfig.text, bgSolid: colorConfig.bg, isPast: false, isPersonal: true, label: show.title || 'Event' };
      }
      const category = show.personalCategory || 'other';
      const config = PERSONAL_CATEGORY_CONFIG[category];
      return { bg: config.bgLight, border: config.border, text: config.textColor, bgSolid: config.color, isPast: false, isPersonal: true, label: show.title || 'Event' };
    }
    
    if (isPast) return { bg: 'bg-gray-100 dark:bg-gray-800', border: 'border-gray-400 dark:border-gray-600', text: 'text-gray-500 dark:text-gray-400', bgSolid: 'bg-gray-200 dark:bg-gray-700', isPast: true, isPersonal: false };
    
    const status = show.status || 'confirmed';
    switch (status) {
      case 'tentative': return { bg: 'bg-yellow-100 dark:bg-yellow-900/30', border: 'border-yellow-500', text: 'text-yellow-800 dark:text-yellow-200', bgSolid: 'bg-yellow-200 dark:bg-yellow-800', isPast: false, isPersonal: false };
      case 'confirmed': return { bg: 'bg-blue-100 dark:bg-blue-900/30', border: 'border-blue-500', text: 'text-blue-800 dark:text-blue-200', bgSolid: 'bg-blue-200 dark:bg-blue-800', isPast: false, isPersonal: false };
      case 'completed': return { bg: 'bg-green-100 dark:bg-green-900/30', border: 'border-green-500', text: 'text-green-800 dark:text-green-200', bgSolid: 'bg-green-200 dark:bg-green-800', isPast: false, isPersonal: false };
      default: return { bg: 'bg-gray-100 dark:bg-gray-800', border: 'border-gray-500', text: 'text-gray-800 dark:text-gray-200', bgSolid: 'bg-gray-200 dark:bg-gray-700', isPast: false, isPersonal: false };
    }
  };

  const getDateStatusColor = (dateKey) => {
    const dayShows = shows[dateKey] || [];
    if (dayShows.length === 0) return null;
    const showDate = new Date(dateKey);
    const isPast = showDate < today;
    if (isPast) return 'past';
    const hasPersonal = dayShows.some(s => s.eventType === 'personal' || s.eventType === 'other');
    if (hasPersonal && dayShows.length === 1) {
      const personalEvent = dayShows.find(s => s.eventType === 'personal' || s.eventType === 'other');
      if (personalEvent.eventColor) return `custom-${personalEvent.eventColor}`;
      return `personal-${personalEvent.personalCategory || 'other'}`;
    }
    const hasTentative = dayShows.some(s => s.eventType !== 'personal' && s.eventType !== 'other' && (s.status || 'confirmed') === 'tentative');
    const hasConfirmed = dayShows.some(s => s.eventType !== 'personal' && s.eventType !== 'other' && (s.status || 'confirmed') === 'confirmed');
    if (hasTentative) return 'tentative';
    if (hasConfirmed) return 'confirmed';
    return 'completed';
  };

  const getDateDotColor = (status) => {
    if (status?.startsWith('custom-')) { const colorName = status.replace('custom-', ''); return EVENT_COLOR_MAP[colorName]?.bg || 'bg-gray-400'; }
    if (status?.startsWith('personal-')) { const category = status.replace('personal-', ''); return PERSONAL_CATEGORY_CONFIG[category]?.color || 'bg-purple-400'; }
    switch (status) { case 'past': return 'bg-gray-400'; case 'tentative': return 'bg-yellow-500'; case 'confirmed': return 'bg-blue-500'; case 'completed': return 'bg-green-500'; default: return 'bg-gray-400'; }
  };

  const getDateBgColor = (status, isSelected, isTodayDate) => {
    if (isSelected) return 'bg-blue-600 text-white shadow-lg scale-105';
    if (isTodayDate) return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 ring-2 ring-purple-400';
    if (status?.startsWith('custom-')) { const colorName = status.replace('custom-', ''); const c = EVENT_COLOR_MAP[colorName]; return `${c?.bgLight} ${c?.text}`; }
    if (status?.startsWith('personal-')) { const category = status.replace('personal-', ''); const c = PERSONAL_CATEGORY_CONFIG[category]; return `${c?.bgLight} ${c?.textColor}`; }
    switch (status) { case 'past': return 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'; case 'tentative': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'; case 'confirmed': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'; case 'completed': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'; default: return 'text-gray-700 dark:text-gray-300'; }
  };

  const clearForm = useCallback(() => {
    setShowForm({ title: '', venue: '', city: '', loadInTime: '', showTime: '', guarantee: '', cashMerch: '', venmoMerch: '', contactName: '', contactEmail: '', contactPhone: '', contactTags: [], eventType: 'show', personalCategory: 'travel', startTime: '', endTime: '', eventColor: 'orange', gas: '', hotel: '', food: '', tolls: '', otherExpenses: '', showNotes: '', setlist: '', crowdInfo: '', technicalNotes: '', nextTime: '', status: 'confirmed', images: [] });
  }, []);

  const handleAddOrUpdateShow = useCallback(() => {
    if (showFormRef.current.title.trim() && selectedDate) {
      if (editingShow) { onUpdateShow(selectedDate.key, editingShow, showFormRef.current); haptics.success(); setEditingShow(null); }
      else { onAddShow(selectedDate.key, showFormRef.current); haptics.success(); }
      clearForm();
      setMobileDetailOpen(false);
    } else { haptics.error(); }
  }, [selectedDate, editingShow, onUpdateShow, onAddShow, clearForm]);

  const handleCancelEditing = useCallback(() => { haptics.light(); setEditingShow(null); clearForm(); }, [clearForm]);
  const handleOpenContact = useCallback((show) => { haptics.light(); setSelectedContact(show); setContactModalOpen(true); }, []);
  const handleCloseContact = useCallback(() => { haptics.light(); setContactModalOpen(false); setSelectedContact(null); }, []);
  const handleNavigate = useCallback((destination) => { haptics.light(); onNavigate(destination); }, [onNavigate]);
  const handleViewModeChange = useCallback((mode) => { haptics.medium(); setViewMode(mode); }, []);
  const handleShowUpdate = useCallback((dateKey, showId, updatedData) => { onUpdateShow(dateKey, showId, updatedData); }, [onUpdateShow]);
  // handleCloseMobileDetail is now defined above, before the swipe hook
  const handleOpenDetailModal = (show) => { haptics.light(); setShowDetailModal({ ...show, dateKey: selectedDate?.key }); };
  const handleCloseDetailModal = () => { haptics.light(); setShowDetailModal(null); };
  const handleStartEditing = (show) => {
    haptics.light();
    setEditingShow(show.id);
    setShowForm({ title: show.title, venue: show.venue || '', city: show.city || '', loadInTime: show.loadInTime || '', showTime: show.showTime || '', guarantee: show.guarantee || '', cashMerch: show.cashMerch || '', venmoMerch: show.venmoMerch || '', contactName: show.contactName || '', contactEmail: show.contactEmail || '', contactPhone: show.contactPhone || '', contactTags: show.contactTags || [], eventType: show.eventType || 'show', personalCategory: show.personalCategory || 'travel', startTime: show.startTime || '', endTime: show.endTime || '', eventColor: show.eventColor || 'orange', gas: show.gas || '', hotel: show.hotel || '', food: show.food || '', tolls: show.tolls || '', otherExpenses: show.otherExpenses || '', showNotes: show.showNotes || '', setlist: show.setlist || '', crowdInfo: show.crowdInfo || '', technicalNotes: show.technicalNotes || '', nextTime: show.nextTime || '', status: show.status || 'confirmed', images: show.images || [] });
  };

  if (loading) return <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4"><div className="max-w-7xl mx-auto"><CardSkeleton /></div></div>;

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const getWeekStart = (date) => { const d = new Date(date); d.setDate(d.getDate() - d.getDay()); return d; };
  const getWeekDays = () => { const weekStart = getWeekStart(currentDate); return Array.from({ length: 7 }, (_, i) => { const day = new Date(weekStart); day.setDate(weekStart.getDate() + i); return day; }); };
  const previousMonth = () => { haptics.light(); setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)); };
  const nextMonth = () => { haptics.light(); setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)); };
  const previousWeek = () => { haptics.light(); const newDate = new Date(currentDate); newDate.setDate(newDate.getDate() - 7); setCurrentDate(newDate); };
  const nextWeek = () => { haptics.light(); const newDate = new Date(currentDate); newDate.setDate(newDate.getDate() + 7); setCurrentDate(newDate); };
  const goToToday = () => { haptics.medium(); setCurrentDate(new Date()); };
  const handleDateClick = (day, month = currentDate.getMonth(), year = currentDate.getFullYear()) => { haptics.selection(); const date = new Date(year, month, day); const dateKey = getDateKey(date); setSelectedDate({ year, month, day, key: dateKey }); clearForm(); setEditingShow(null); };
  const isToday = (date) => { const d = new Date(date); d.setHours(0, 0, 0, 0); return d.getTime() === today.getTime(); };
  const isTodayByDay = (day) => currentDate.getFullYear() === today.getFullYear() && currentDate.getMonth() === today.getMonth() && day === today.getDate();
  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const calendarDays = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const weekDays = getWeekDays();
  const getWeekRangeText = () => { const ws = weekDays[0], we = weekDays[6]; return ws.getMonth() === we.getMonth() ? `${monthsShort[ws.getMonth()]} ${ws.getDate()}-${we.getDate()}` : `${monthsShort[ws.getMonth()]} ${ws.getDate()} - ${monthsShort[we.getMonth()]} ${we.getDate()}`; };
  const sortEventsByTime = (events) => {
    if (!events || events.length === 0) return [];
    return [...events].sort((a, b) => {
      const timeA = a.eventType === 'other' || a.eventType === 'personal' ? (a.startTime || '') : (a.showTime || a.loadInTime || '');
      const timeB = b.eventType === 'other' || b.eventType === 'personal' ? (b.startTime || '') : (b.showTime || b.loadInTime || '');
      if (!timeA && !timeB) return 0; if (!timeA) return 1; if (!timeB) return -1;
      return timeA.localeCompare(timeB);
    });
  };

  const ShowsList = () => selectedDate && (
    <div className="space-y-4">
      {shows[selectedDate.key]?.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Events on this day ({shows[selectedDate.key].length})</h4>
          <div className="space-y-2">
            {sortEventsByTime(shows[selectedDate.key]).map(show => { 
              const colors = getShowStatusColors(show, selectedDate.key);
              const isEditing = editingShow === show.id;
              const isOtherEvent = show.eventType === 'other' || show.eventType === 'personal';
              return (
                <div key={show.id} onClick={() => { if (!isEditing) handleOpenDetailModal(show); }} className={`p-4 rounded-xl border ${colors.border} ${colors.bg} cursor-pointer hover:shadow-md transition-shadow ${isEditing ? 'ring-2 ring-blue-500' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className={`font-semibold ${colors.isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}>{show.title}</p>
                        {!colors.isPersonal && <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bgSolid} ${colors.text}`}>{colors.isPast ? 'past' : (show.status || 'confirmed')}</span>}
                        {isEditing && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500 text-white">Editing</span>}
                      </div>
                      {!colors.isPersonal && show.venue && <p className={`text-sm ${colors.isPast ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>{show.venue}</p>}
                      {colors.isPersonal && show.city && <p className={`text-sm ${colors.text}`}>📍 {show.city}</p>}
                      {!colors.isPersonal && show.showTime && <p className={`text-sm ${colors.isPast ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>🕒 {formatTime12h(show.showTime)}</p>}
                      {isOtherEvent && show.startTime && <p className={`text-sm ${colors.isPast ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>🕒 {formatTime12h(show.startTime)}{show.endTime && ` - ${formatTime12h(show.endTime)}`}</p>}
                    </div>
                    <ChevronRight size={20} className="text-gray-400 flex-shrink-0 mt-1" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {editingShow && (
        <button onClick={() => { haptics.light(); clearForm(); setEditingShow(null); }} className="w-full py-3 border-2 border-dashed border-blue-400 dark:border-blue-600 rounded-xl text-blue-600 dark:text-blue-400 font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-center gap-2">
          <Plus size={20} />Add Another Event
        </button>
      )}
    </div>
  );

  const renderEventCard = (show, dateKey, onClick) => {
    const colors = getShowStatusColors(show, dateKey);
    const isOtherEvent = show.eventType === 'other' || show.eventType === 'personal';
    return (
      <div key={show.id} className={`p-4 rounded-xl border-l-4 shadow-sm ${colors.bg} ${colors.border}`}>
        <div className="flex items-start gap-3">
          <div className="flex-1" onClick={onClick}>
            <div className="flex items-center gap-2 mb-1">
              <p className={`font-bold ${colors.isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}>{show.title}</p>
              {!colors.isPersonal && <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bgSolid} ${colors.text}`}>{colors.isPast ? 'past' : (show.status || 'confirmed')}</span>}
            </div>
            {!colors.isPersonal && show.venue && <p className={`text-sm ${colors.isPast ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}`}>{show.venue}{show.city && `, ${show.city}`}</p>}
            {colors.isPersonal && show.city && <p className={`text-sm ${colors.text}`}>📍 {show.city}</p>}
            {!colors.isPersonal && show.showTime && <p className={`text-sm mt-1 ${colors.isPast ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>🕒 {formatTime12h(show.showTime)}</p>}
            {isOtherEvent && show.startTime && <p className={`text-sm mt-1 ${colors.isPast ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>🕒 {formatTime12h(show.startTime)}{show.endTime && ` - ${formatTime12h(show.endTime)}`}</p>}
          </div>
          <button onClick={(e) => { e.stopPropagation(); handleStartEditing(show); setMobileDetailOpen(true); }} className="p-2 hover:bg-white/50 dark:hover:bg-gray-700/50 rounded-lg"><Edit2 size={18} className="text-gray-500" /></button>
        </div>
      </div>
    );
  };

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 pb-20 md:pb-0"
      style={{ overscrollBehavior: 'none' }}
    >
      {/* Mobile Sticky Header */}
      <div className="sticky top-0 z-40 bg-white dark:bg-gray-800 shadow-md md:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => handleNavigate('dashboard')} className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 active:bg-gray-200 dark:active:bg-gray-600 transition-colors"><ChevronLeft size={24} /></button>
          <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">Calendar</h1>
          <div className="flex bg-gray-200 dark:bg-gray-700 rounded-xl p-1">
            <button onClick={() => handleViewModeChange('month')} className={`p-2 rounded-lg ${viewMode === 'month' ? 'bg-white dark:bg-gray-600 shadow-sm' : ''}`}><Grid size={20} className={viewMode === 'month' ? 'text-gray-800 dark:text-gray-100' : 'text-gray-500'} /></button>
            <button onClick={() => handleViewModeChange('week')} className={`p-2 rounded-lg ${viewMode === 'week' ? 'bg-white dark:bg-gray-600 shadow-sm' : ''}`}><Rows size={20} className={viewMode === 'week' ? 'text-gray-800 dark:text-gray-100' : 'text-gray-500'} /></button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-2 md:p-8">
        <div className="md:hidden flex items-center justify-center gap-2 text-xs mb-3 px-1 flex-wrap">
          <div className="px-2 py-1 rounded bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 font-medium">Tentative</div>
          <div className="px-2 py-1 rounded bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 font-medium">Confirmed</div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 md:p-6">
          {/* Desktop header */}
          <div className="hidden md:flex md:items-center md:justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button onClick={() => handleViewModeChange('month')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${viewMode === 'month' ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-800 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}><Grid size={16} />Month</button>
                <button onClick={() => handleViewModeChange('week')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${viewMode === 'week' ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-800 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}><Rows size={16} />Week</button>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleNavigate('dashboard')} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"><CalIcon size={20} />Home</button>
              <button onClick={() => handleNavigate('timeline')} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"><List size={20} />Timeline</button>
              <button onClick={() => handleNavigate('summary')} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"><DollarSign size={20} />Summary</button>
              <button onClick={() => handleNavigate('expenses')} className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"><Receipt size={20} />Expenses</button>
              <button onClick={() => handleNavigate('inventory')} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"><Package size={20} />Inventory</button>
            </div>
          </div>
          
          {/* Mobile Week View */}
          {viewMode === 'week' ? (
            <div 
              className="md:hidden"
              style={{ touchAction: 'pan-y pinch-zoom', overscrollBehavior: 'none' }}
            >
              <div className="flex items-center justify-between mb-3">
                <button onClick={previousWeek} className="p-3 -ml-3 active:bg-gray-200 dark:active:bg-gray-700 rounded-lg"><ChevronLeft size={24} className="text-gray-600 dark:text-gray-300" /></button>
                <div className="text-center">
                  <h2 className="text-base font-bold text-gray-800 dark:text-gray-100">{getWeekRangeText()}</h2>
                  <button onClick={goToToday} className="text-xs text-blue-600 dark:text-blue-400">Today</button>
                </div>
                <button onClick={nextWeek} className="p-3 -mr-3 active:bg-gray-200 dark:active:bg-gray-700 rounded-lg"><ChevronRight size={24} className="text-gray-600 dark:text-gray-300" /></button>
              </div>
              <div 
                {...weekSwipeHandlers}
                style={weekSwipeStyle}
                className="flex gap-1.5 mb-4"
              >
                {weekDays.map((date, index) => {
                  const dateKey = getDateKey(date);
                  const dayShows = shows[dateKey] || [];
                  const hasShows = dayShows.length > 0;
                  const isSelected = selectedDate?.key === dateKey;
                  const isTodayDate = isToday(date);
                  const dateStatus = getDateStatusColor(dateKey);
                  return (
                    <button key={index} onClick={() => handleDateClick(date.getDate(), date.getMonth(), date.getFullYear())} className={`flex-1 py-3 px-1 rounded-xl text-center transition-all active:scale-95 ${isSelected ? 'bg-blue-600 text-white shadow-lg' : getDateBgColor(dateStatus, false, isTodayDate)}`}>
                      <p className="text-[10px] font-medium opacity-70">{daysOfWeekShort[index]}</p>
                      <p className="text-xl font-bold">{date.getDate()}</p>
                      {hasShows && !isSelected && <div className="flex gap-0.5 justify-center mt-1">{dayShows.slice(0, 3).map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full ${getDateDotColor(dateStatus)}`} />)}</div>}
                    </button>
                  );
                })}
              </div>
              {selectedDate && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">{months[selectedDate.month]} {selectedDate.day}</h3>
                  </div>
                  {shows[selectedDate.key]?.length > 0 ? (
                    <div className="space-y-2">
                      {sortEventsByTime(shows[selectedDate.key]).map(show => renderEventCard(show, selectedDate.key, () => handleOpenDetailModal(show)))}
                      <AddShowGate currentCount={Object.values(shows).flat().length}>
                        <button onClick={() => { haptics.light(); setMobileDetailOpen(true); }} className="w-full mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2">
                          <Plus size={18} />Add Event
                        </button>
                      </AddShowGate>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <CalIcon size={36} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">No events scheduled</p>
                      <AddShowGate currentCount={Object.values(shows).flat().length}>
                        <button onClick={() => { haptics.light(); setMobileDetailOpen(true); }} className="px-8 py-2.5 bg-blue-600 text-white rounded-lg font-medium">
                          Add an event
                        </button>
                      </AddShowGate>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div 
              className="md:hidden"
              style={{ touchAction: 'pan-y pinch-zoom', overscrollBehavior: 'none' }}
            >
              <div className="flex items-center justify-between mb-3">
                <button onClick={previousMonth} className="p-3 -ml-3 active:bg-gray-200 dark:active:bg-gray-700 rounded-lg"><ChevronLeft size={24} className="text-gray-600 dark:text-gray-300" /></button>
                <div className="text-center">
                  <h2 className="text-base font-bold text-gray-800 dark:text-gray-100">{months[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
                  <button onClick={goToToday} className="text-xs text-blue-600 dark:text-blue-400">Today</button>
                </div>
                <button onClick={nextMonth} className="p-3 -mr-3 active:bg-gray-200 dark:active:bg-gray-700 rounded-lg"><ChevronRight size={24} className="text-gray-600 dark:text-gray-300" /></button>
              </div>
              <div 
                {...monthSwipeHandlers}
                style={monthSwipeStyle}
              >
                <div className="grid grid-cols-7 gap-1 mb-1">{daysOfWeekShort.map((day, i) => <div key={i} className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 py-2">{day}</div>)}</div>
                <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => {
                  if (day === null) return <div key={`empty-${index}`} className="aspect-square" />;
                  const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                  const dateKey = getDateKey(date);
                  const dayShows = shows[dateKey] || [];
                  const hasShows = dayShows.length > 0;
                  const isSelected = selectedDate?.key === dateKey;
                  const isTodayDate = isTodayByDay(day);
                  const dateStatus = getDateStatusColor(dateKey);
                  return (
                    <button key={day} onClick={() => handleDateClick(day)} className={`aspect-square rounded-xl text-sm font-semibold relative flex flex-col items-center justify-center transition-all active:scale-90 ${getDateBgColor(dateStatus, isSelected, isTodayDate)}`}>
                      <span>{day}</span>
                      {hasShows && !isSelected && <div className="absolute bottom-1 flex gap-0.5">{dayShows.slice(0, 3).map((_, i) => <div key={i} className={`w-1 h-1 rounded-full ${getDateDotColor(dateStatus)}`} />)}</div>}
                    </button>
                  );
                })}
                </div>
              </div>
              
              {/* Mobile Inline Events for Selected Date */}
              {selectedDate && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">{months[selectedDate.month]} {selectedDate.day}</h3>
                  </div>
                  {shows[selectedDate.key]?.length > 0 ? (
                    <div className="space-y-2">
                      {sortEventsByTime(shows[selectedDate.key]).map(show => renderEventCard(show, selectedDate.key, () => handleOpenDetailModal(show)))}
                      <AddShowGate currentCount={Object.values(shows).flat().length}>
                        <button onClick={() => { haptics.light(); setMobileDetailOpen(true); }} className="w-full mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2">
                          <Plus size={18} />Add Event
                        </button>
                      </AddShowGate>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <CalIcon size={36} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">No events scheduled</p>
                      <AddShowGate currentCount={Object.values(shows).flat().length}>
                        <button onClick={() => { haptics.light(); setMobileDetailOpen(true); }} className="px-8 py-2.5 bg-blue-600 text-white rounded-lg font-medium">
                          Add an event
                        </button>
                      </AddShowGate>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Desktop view */}
          <div className="hidden md:grid md:grid-cols-1 lg:grid-cols-3 gap-6">
            {viewMode === 'month' ? (
              <div className="hidden md:block lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <button onClick={previousMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><ChevronLeft size={24} className="text-gray-600 dark:text-gray-300" /></button>
                  <div className="text-center">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">{months[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
                    <button onClick={goToToday} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">Today</button>
                  </div>
                  <button onClick={nextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><ChevronRight size={24} className="text-gray-600 dark:text-gray-300" /></button>
                </div>
                <div className="grid grid-cols-7 gap-2 mb-2">{daysOfWeek.map(day => <div key={day} className="text-center font-semibold text-sm text-gray-600 dark:text-gray-400 py-2">{day}</div>)}</div>
                <div className="grid grid-cols-7 gap-2">
                  {calendarDays.map((day, index) => {
                    if (day === null) return <div key={`empty-${index}`} className="min-h-[100px]" />;
                    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                    const dateKey = getDateKey(date);
                    const dayShows = shows[dateKey] || [];
                    const hasShows = dayShows.length > 0;
                    const isSelected = selectedDate?.key === dateKey;
                    const isTodayDate = isTodayByDay(day);
                    return (
                      <button key={day} onClick={() => { haptics.selection(); setSelectedDate({ year: currentDate.getFullYear(), month: currentDate.getMonth(), day, key: dateKey }); clearForm(); setEditingShow(null); }} className={`min-h-[100px] p-2 rounded-lg transition-all hover:scale-[1.02] border-2 ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : isTodayDate ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`}>
                        <span className={`text-sm font-semibold ${isSelected ? 'text-blue-600 dark:text-blue-400' : isTodayDate ? 'text-purple-600 dark:text-purple-400' : 'text-gray-700 dark:text-gray-300'}`}>{day}</span>
                        {hasShows && <div className="mt-1 space-y-1">{sortEventsByTime(dayShows).slice(0, 2).map(show => { const colors = getShowStatusColors(show, dateKey); return <div key={show.id} className={`text-xs px-1.5 py-0.5 rounded truncate ${colors.bgSolid} ${colors.text}`}>{show.title}</div>; })}{dayShows.length > 2 && <p className="text-xs text-gray-500 dark:text-gray-400">+{dayShows.length - 2} more</p>}</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="hidden md:block lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <button onClick={previousWeek} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><ChevronLeft size={24} className="text-gray-600 dark:text-gray-300" /></button>
                  <div className="text-center">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">{getWeekRangeText()}</h2>
                    <button onClick={goToToday} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">Today</button>
                  </div>
                  <button onClick={nextWeek} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><ChevronRight size={24} className="text-gray-600 dark:text-gray-300" /></button>
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {weekDays.map((date, index) => {
                    const dateKey = getDateKey(date);
                    const dayShows = shows[dateKey] || [];
                    const hasShows = dayShows.length > 0;
                    const isSelected = selectedDate?.key === dateKey;
                    const isTodayDate = isToday(date);
                    return (
                      <div key={index} className="flex flex-col">
                        <div className={`text-center py-2 rounded-t-lg ${isTodayDate ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{daysOfWeek[index]}</p>
                          <p className={`text-lg font-bold ${isTodayDate ? 'text-purple-600 dark:text-purple-400' : 'text-gray-800 dark:text-gray-100'}`}>{date.getDate()}</p>
                        </div>
                        <button onClick={() => { haptics.selection(); setSelectedDate({ year: date.getFullYear(), month: date.getMonth(), day: date.getDate(), key: dateKey }); clearForm(); setEditingShow(null); }} className={`flex-1 min-h-[120px] p-2 border-2 rounded-b-lg transition-all ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
                          {hasShows ? <div className="space-y-1">{sortEventsByTime(dayShows).slice(0, 3).map(show => { const colors = getShowStatusColors(show, dateKey); return <div key={show.id} className={`text-xs p-1.5 rounded truncate ${colors.bgSolid} ${colors.text}`}><p className="font-medium truncate">{show.title}</p></div>; })}{dayShows.length > 3 && <p className="text-xs text-gray-500 text-center">+{dayShows.length - 3}</p>}</div> : <div className="h-full flex items-center justify-center"><Plus size={20} className="text-gray-300 dark:text-gray-600" /></div>}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Desktop sidebar */}
            <div className="hidden lg:block lg:col-span-1">
              {selectedDate ? (
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 sticky top-4 space-y-4">
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">{months[selectedDate.month]} {selectedDate.day}, {selectedDate.year}</h3>
                  <ShowsList />
                  {shows[selectedDate.key]?.length > 0 && !editingShow && <div className="border-t border-gray-300 dark:border-gray-600 pt-4"><h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Add New Event</h4></div>}
                  {editingShow && <div className="border-t border-gray-300 dark:border-gray-600 pt-4"><h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Edit Event</h4></div>}
                  {!shows[selectedDate.key]?.length && !editingShow && <h4 className="font-semibold text-gray-700 dark:text-gray-300">Add New Event</h4>}
                  <ShowFormComponent showForm={showForm} setShowForm={setShowForm} editingShow={editingShow} onSubmit={handleAddOrUpdateShow} onCancel={handleCancelEditing} isPastOrCompleted={isPastOrCompleted} />
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-center"><p className="text-gray-500 dark:text-gray-400">Select a date to add or view events</p></div>
              )}
            </div>
          </div>
        </div>
        
        {/* Mobile Detail Panel with Swipe to Close */}
        <div className={`md:hidden fixed inset-0 z-50 transition-all duration-300 ${mobileDetailOpen ? 'visible' : 'invisible pointer-events-none'}`}>
          <div 
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${mobileDetailOpen ? 'opacity-50' : 'opacity-0'}`} 
            onClick={handleCloseMobileDetail} 
          />
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl max-h-[90vh] overflow-hidden"
            style={{
              transform: mobileDetailOpen 
                ? `translateY(${dragOffset}px)` 
                : 'translateY(100%)',
              transition: isDragging ? 'none' : 'transform 0.3s ease-out',
            }}
          >
            {/* Swipeable Drag Handle */}
            <div 
              className="flex flex-col items-center pt-3 pb-1 cursor-grab active:cursor-grabbing"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{ touchAction: 'none' }}
            >
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Swipe down to close</p>
            </div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                {selectedDate ? `${months[selectedDate.month]} ${selectedDate.day}` : 'Select a date'}
              </h3>
              <button 
                onClick={handleCloseMobileDetail} 
                className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Content */}
            <div 
  className="overflow-y-auto p-5" 
  style={{ 
    maxHeight: 'calc(90vh - 80px)',
    touchAction: 'pan-y',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch'
  }}
>
              {selectedDate && (
                <div className="space-y-4">
                  <ShowsList />
                  
                  {shows[selectedDate.key]?.length > 0 && !editingShow && (
                    <div className="border-t border-gray-300 dark:border-gray-600 pt-4">
                      <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Add New Event</h4>
                    </div>
                  )}
                  
                  {editingShow && (
                    <div className="border-t border-gray-300 dark:border-gray-600 pt-4">
                      <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Edit Event</h4>
                    </div>
                  )}
                  
                  {!shows[selectedDate.key]?.length && !editingShow && (
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Add New Event</h4>
                  )}
                  
                  <ShowFormComponent 
                    showForm={showForm} 
                    setShowForm={setShowForm} 
                    editingShow={editingShow} 
                    onSubmit={handleAddOrUpdateShow} 
                    onCancel={handleCancelEditing}
                    isPastOrCompleted={isPastOrCompleted}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <ContactModal contact={selectedContact} isOpen={contactModalOpen} onClose={handleCloseContact} />

        {showDetailModal && (
          <ShowDetailModal
            show={showDetailModal}
            isOpen={!!showDetailModal}
            onClose={handleCloseDetailModal}
            onUpdate={handleShowUpdate}
            onDelete={(dateKey, showId) => { if (onDeleteShow) onDeleteShow(dateKey, showId); setShowDetailModal(null); }}
            salesLog={salesLog}
            onNavigate={onNavigate}
            documents={documents}
          />
        )}

        {deletedShow && (
          <div className="fixed bottom-24 md:bottom-8 left-1/2 transform -translate-x-1/2 z-50" style={{ animation: 'slideUp 0.3s ease-out' }}>
            <div className="bg-gray-900 dark:bg-gray-700 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-4">
              <span className="text-sm">Event deleted</span>
              <button onClick={handleUndoDelete} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors">Undo</button>
            </div>
          </div>
        )}
        
        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translate(-50%, 20px); }
            to { opacity: 1; transform: translate(-50%, 0); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default CalendarComponent;